import React, {useEffect, useRef, useState} from "react";

import {useMutation} from "@tanstack/react-query";
import {Form} from "react-final-form";

import {OrderDishItem} from "@globals/DailyOrders/interfaces";
import {useSiteConfig} from "@globals/SiteConfig";
import {EventOrigin, recordEvent} from "@globals/config/eventUtils";
import {getResolutionImgUrl} from "@globals/functions/getResolutionImgUrl";

import FavoriteIcon from "@components/FavoriteIcon";
import {StarsField} from "@components/forms";

import DishFullReview from "./DishFullReview";
import styles from "./DishReviewForm.scss";
import {putDishReview} from "./DishReviewService";
import useReviewChoices from "./hooks/useReviewChoices";
import {DishReviewResponse} from "./interfaces";

type DishReviewFormProps = {
  orderItem: OrderDishItem;
  onSuccessReview?: (data: DishReviewResponse) => void;
  closeRegion: () => void;
  setDismissBtnClassName: (className: string) => void;
};

type DishReviewValues = {
  rating: number;
  comment?: string | null;
};

export enum ReviewStep {
  StarsReview = 1,
  FullReview,
  ReviewComplete,
}

export default function DishReviewForm({
  orderItem,
  onSuccessReview,
  closeRegion,
  setDismissBtnClassName,
}: DishReviewFormProps): JSX.Element {
  const [currentStep, setCurrentStep] = useState<ReviewStep>(
    ReviewStep.StarsReview
  );
  const {
    availableChoices,
    setChoicesRating,
    selectedChoices,
    setSelectedChoices,
    formattedChoices,
    isLoading: isChoicesLoading,
  } = useReviewChoices(orderItem.rating);
  const closingTimer = useRef<NodeJS.Timeout>();

  useEffect(() => {
    // record once when this form opened;
    recordEvent(EventOrigin.PAGE, "view own review", orderItem.id);

    // when user opens for edit;
    if (orderItem.rating) {
      setCurrentStep(ReviewStep.FullReview);
    }

    // set original review choices from orderItem;
    if (orderItem.review_choices) {
      setSelectedChoices(orderItem.review_choices.map((choice) => choice.id));
    }

    // reset button class;
    setDismissBtnClassName("");

    return () => {
      // cancel auto-close on unmount
      clearTimeout(closingTimer.current as NodeJS.Timeout);
    };
  }, []);

  const {mutate: sendReview} = useMutation({
    mutationFn: (values: DishReviewValues) => {
      const payload = {
        id: orderItem.id,
        rating: values.rating,
        comments: values.comment ?? undefined,
        review_choices: formattedChoices,
      };
      return putDishReview(payload);
    },
    onSuccess: (data) => {
      if (orderItem.rating) {
        recordEvent(EventOrigin.PAGE, "modify review", orderItem.id);
      } else {
        recordEvent(EventOrigin.PAGE, "create review", orderItem.id);
      }

      // change step;
      if (currentStep === ReviewStep.StarsReview) {
        setCurrentStep(ReviewStep.FullReview);
      } else {
        setCurrentStep(ReviewStep.ReviewComplete);
        closingTimer.current = setTimeout(() => closeRegion(), 10000);
        setDismissBtnClassName(styles.dismissWhite);
      }

      // outer callback
      if (typeof onSuccessReview === "function") {
        onSuccessReview(data);
      }
    },
  });

  const onReviewSubmit = (values: DishReviewValues) => {
    sendReview(values);
  };

  const onStarClick = (rating: number) => {
    // update rating in our hook to fetch new choices;
    setChoicesRating(rating);
  };

  const {getDishReviewTitle} = useSiteConfig();
  const dishReviewTitle = getDishReviewTitle();

  const containsUnsupportedChars = (text: string): boolean => {
    // This regex targets 4-byte characters (surrogate pairs)
    return /[\uD800-\uDBFF][\uDC00-\uDFFF]/.test(text);
  };

  return (
    <>
      {(currentStep === ReviewStep.StarsReview ||
        currentStep === ReviewStep.FullReview) && (
        <div className={styles.dishReviewContainer}>
          <div className={styles.img}>
            <img src={getResolutionImgUrl(orderItem.item.photo.url)} alt="" />
          </div>
          <div className={styles.form}>
            <h3 className={styles.reviewTitle}>{dishReviewTitle}</h3>
            <div className={styles.itemName}>
              <span>{orderItem.item.item}</span>
              <FavoriteIcon
                itemId={orderItem.item.item_id}
                className={styles.favoriteIcon}
              />
            </div>
            <div className={styles.restaurantName}>
              From: <span>{orderItem.item.restaurant.name}</span>
            </div>
            <Form
              onSubmit={onReviewSubmit}
              initialValues={{
                rating: orderItem.rating || 0,
                comment: orderItem.comment as string,
              }}
              validate={(values) => {
                const errors: any = {};
                if (containsUnsupportedChars(values.comment ?? "")) {
                  errors.comment = "Emojis are not supported.";
                }
                return errors;
              }}
              render={({values, handleSubmit}) => (
                <form onSubmit={handleSubmit}>
                  <StarsField
                    name="rating"
                    showTooltip
                    validate={(value) => onStarClick(value)}
                    className={styles.starsContainer}
                    // to submit form on click we need to wait for onChange to set the value;
                    onStarClick={() =>
                      setTimeout(() => {
                        currentStep === ReviewStep.StarsReview &&
                          handleSubmit();
                      }, 0)
                    }
                  />
                  {/* to submit first step with Enter button from keyboard */}
                  {currentStep === ReviewStep.StarsReview && (
                    <input type="submit" className={styles.hidden} />
                  )}

                  {/* STEP 2 after star submit */}
                  {currentStep === ReviewStep.FullReview && (
                    <DishFullReview
                      isChoicesLoading={isChoicesLoading}
                      availableChoices={availableChoices}
                      selectedChoices={selectedChoices}
                      setSelectedChoices={setSelectedChoices}
                      hasComment={!!orderItem.comment || !!values.comment}
                      rating={values.rating}
                    />
                  )}
                </form>
              )}
            />
          </div>
        </div>
      )}

      {/* STEP 3 */}
      {currentStep === ReviewStep.ReviewComplete && (
        <div className={styles.completedReview}>
          <h3 className={styles.reviewTitle}>Thank you</h3>
          <p className={styles.completedMessage}>
            Your feedback will help us improve!
          </p>
        </div>
      )}
    </>
  );
}
